import { FLAGS, SystemConnection } from '@Types';
import {
	ConnectionFormField,
	ConnStrReplacer,
} from '@Utils/connectionStringReplacer';
import { useIsFeatureEnabled } from '@innovyze/stylovyze';
import { useEffect, useState } from 'react';

type ConnectionStringReturn = {
	connectionString: string;
};

const FIELD_REMAP: Record<
	'ODBC' | 'OLEDB',
	Partial<Record<ConnectionFormField, ConnectionFormField>>
> = {
	ODBC: {
		portOledb: 'port',
		serverTypeOledb: 'serverType',
		serverAddressOledb: 'serverAddress',
	},
	OLEDB: {
		port: 'portOledb',
		serverType: 'serverTypeOledb',
		serverAddress: 'serverAddressOledb',
	},
};

const treatFields = (formData: SystemConnection, isOledbEnabled: boolean) => {
	const { connectionType } = formData;
	const fields = Object.entries(formData).map(([field, value]) => {
		const ret = { name: field as ConnectionFormField, value };
		const isOledb = isOledbEnabled && connectionType === 'OLEDB';
		const isOdbc = connectionType === 'ODBC';
		if (isOledb) ret.name = FIELD_REMAP.OLEDB[ret.name] || ret.name;
		if (isOdbc) ret.name = FIELD_REMAP.ODBC[ret.name] || ret.name;
		return ret;
	});
	return fields;
};

const ensureTrailingSemicolon = (connStr: string) => {
	return connStr.endsWith(';') ? connStr : connStr + ';';
};

const getFields = (formData: SystemConnection, isOledbEnabled: boolean) => {
	const fields = treatFields(formData, isOledbEnabled);
	if (isOledbEnabled && formData.connectionType === 'ODBC') {
		fields.push({ name: 'serverTypeOledb', value: '' }); // ? removes PROVIDER
		fields.push({ name: 'serverAddressOledb', value: '' }); // ? removes Data Source
	}
	if (isOledbEnabled && formData.connectionType === 'OLEDB') {
		fields.push({ name: 'serverAddress', value: '' }); // ? removes "SERVER"
	}
	return fields;
};

export const createConnectionString = (
	formData: SystemConnection,
	isOledbEnabled: boolean,
) => {
	return getFields(formData, isOledbEnabled).reduce(
		(acc, { name, value }) => {
			if (isOledbEnabled && formData.connectionType === 'OLEDB') {
				const par = { str: acc, name, value, fields: formData };
				return ConnStrReplacer.parseOledb(par as never);
			}
			return ConnStrReplacer.replace(acc, name, value);
		},
		formData.connectionString
			? ensureTrailingSemicolon(formData.connectionString)
			: '',
	);
};

export const useConnectionString = (
	formData: SystemConnection,
): ConnectionStringReturn => {
	const isOledbEnabled = !!useIsFeatureEnabled(FLAGS.INFO_360_ADMIN_OLEDB);
	const [connectionString, setConnectionString] = useState<string>('');

	useEffect(() => {
		const connStr = createConnectionString(formData, isOledbEnabled);
		setConnectionString(connStr);
	}, [
		formData.connectionType,
		formData.serverAddress,
		formData.port,
		formData.database,
		formData.serverType,
	]);

	return { connectionString };
};
